import { EffectiveCart } from 'interfaces/cart';
import { PdProductItem } from 'interfaces/line-item';
import { Discount } from 'interfaces/order';
import { useRouter } from 'next/router';
import { useEffect } from 'react';
import { useCart } from '../../contexts/cart';
import { useFilteredCartItems } from '../../hooks/cart/use-filtered-cart-items';
import useMOVConditions from '../../hooks/common/use-mov-conditions';
import { showErrorToast } from '../../utils/toasts';

interface UseCartStateReturnType {
  canUseCoupon: boolean;
  cartTotal: number;
  containsPreorderItem: boolean;
  containsPreorderItemsOnly: boolean;
  containsSubscriptionItem: boolean;
  discount: Discount | undefined;
  effectiveCart: EffectiveCart | undefined;
  effectiveCartErrors: EffectiveCart['errors'] | undefined;
  insufficientMoney: number;
  isCartEmpty: boolean;
  isCartUpdating: boolean;
  isCheckoutBeingCreated: boolean;
  items: PdProductItem[];
  itemsCount: number;
  loading: boolean;
  minimumBasketValue;
  oosItems: PdProductItem[];
  shouldShowMinimumBasketNotice: boolean;
  setCouponCode: (code: string | null) => void;
  goToCheckout: () => Promise<void>;
}

// TODO: this hook is unnecessary to have. We can use useCart directly instead
export const useCartState = (): UseCartStateReturnType => {
  const {
    loading: checkoutLoading,
    canUseCoupon,
    cartTotal,
    containsPreorderItem,
    containsPreorderItemsOnly,
    containsSubscriptionItem,
    discount,
    effectiveCart,
    effectiveCartErrors,
    isCartEmpty,
    isCartUpdating,
    isCheckoutBeingCreated,
    items,
    itemsCount,
    oosItems,
    setCouponCode,
    goToCheckout,
  } = useCart();

  const { insufficientMoney, minimumBasketValue, shouldShowMinimumBasketNotice } = useMOVConditions(
    { effectiveCart }
  );

  // Is savings plan comes from effective cart as an item?
  const filteredItems = useFilteredCartItems(items);
  const { isReady, query } = useRouter();

  useEffect(() => {
    if (!isReady) return;

    const error = Array.isArray(query['error']) ? query['error'][0] : query['error'];
    const source = Array.isArray(query['source']) ? query['source'][0] : query['source'];
    const interactionReason = Array.isArray(query['interactionReason'])
      ? query['interactionReason'][0]
      : query['interactionReason'];

    if (interactionReason === 'DECLINED') {
      showErrorToast({
        error: 'error:express-checkout:common-error',
        caller: 'useCartState',
      });
      return;
    }

    if (error === 'InvalidShippingAddress' && source === 'express-checkout') {
      showErrorToast({
        error: 'error:express-checkout:invalid-address',
        caller: 'useCartState',
      });
      return;
    }

    if (error && source === 'express-checkout') {
      showErrorToast({
        error: 'error:express-checkout:common-error',
        caller: 'useCartState',
      });
    }
  }, [isReady, query]);

  return {
    canUseCoupon,
    cartTotal,
    containsPreorderItem,
    containsPreorderItemsOnly,
    containsSubscriptionItem,
    discount,
    effectiveCart,
    effectiveCartErrors,
    insufficientMoney,
    isCartEmpty,
    isCartUpdating,
    isCheckoutBeingCreated,
    items: filteredItems,
    itemsCount,
    loading: checkoutLoading,
    minimumBasketValue,
    oosItems,
    shouldShowMinimumBasketNotice,
    setCouponCode,
    goToCheckout,
  };
};
